// src/components/affiliate/AffiliateDashboard.jsx
// Main affiliate dashboard component

import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  Link, 
  BarChart3, 
  Settings,
  ExternalLink,
  Copy,
  Check,
  Video,
  Award
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import LiveSellingPlatform from './LiveSellingPlatform';
import AnalyticsDashboard from './AnalyticsDashboard';
import TierSystem from './TierSystem';
import ProductLinkGenerator from './ProductLinkGenerator';
import AffiliateSettings from './AffiliateSettings';

export default function AffiliateDashboard({ userId }) {
  const { notify } = React.useContext(ToastContext);
  const [affiliate, setAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showLiveSelling, setShowLiveSelling] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTierSystem, setShowTierSystem] = useState(false);
  const [showProductLinkGenerator, setShowProductLinkGenerator] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [cashoutAmount, setCashoutAmount] = useState(0);
  const [cashoutMethod, setCashoutMethod] = useState("gcash");
  const [cashoutNotes, setCashoutNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load data only when we have a stable, truthy userId to avoid transient renders
  useEffect(() => {
    if (!userId) {
      // eslint-disable-next-line no-console
      console.debug('[AffiliateDashboard] Skipping load: no userId');
      return;
    }
    loadAffiliateData();
  }, [userId]);

  const loadAffiliateData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      
      // Use Cloud Function to get affiliate dashboard data
      const getAffiliateDashboard = httpsCallable(functions, 'getAffiliateDashboard');
  const result = await getAffiliateDashboard({ userId });
      
      if (!result.data.affiliate) {
        notify('Affiliate profile not found. Please contact support.', { type: 'error' });
        return;
      }
      
  setAffiliate(result.data.affiliate);
      setCommissions(result.data.commissions || []);
      setReferrals(result.data.referrals || []);
  setPayouts(result.data.payouts || []);
      // Validate currently selected cashout method against newly loaded affiliate payout methods
      try {
        const payout = result.data.affiliate?.payout || {};
        const hasGCash = !!(payout.gcashNumber && String(payout.gcashNumber).trim());
        const hasPaymaya = !!(payout.paymayaNumber && String(payout.paymayaNumber).trim());
        const hasBank = !!(payout.bankName && payout.bankAccount && payout.accountName && String(payout.bankAccount).trim());
        const available = [
          ...(hasGCash ? ['gcash'] : []),
          ...(hasPaymaya ? ['paymaya'] : []),
          ...(hasBank ? ['bank'] : []),
        ];
        if (available.length > 0 && !available.includes(cashoutMethod)) {
          setCashoutMethod(available[0]);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug('[AffiliateDashboard] cashout method validation skipped', e);
      }
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      notify('Failed to load affiliate data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const requestCashout = async () => {
    if (!affiliate) return;
    const available = Number(affiliate?.payouts?.availableAmount || 0);
  const minPayout = 50;
    const amt = Number(cashoutAmount || 0);
    if (!amt || amt <= 0) {
      notify("Enter a valid amount", { type: "error" });
      return;
    }
    if (amt > available) {
      notify(`Amount exceeds available balance (₱${available.toFixed(2)})`, { type: "error" });
      return;
    }
    if (amt < minPayout) {
      notify(`Minimum cashout is ₱${minPayout.toFixed(2)}`, { type: "error" });
      return;
    }
    try {
      setSubmitting(true);
      const fn = httpsCallable(functions, 'requestAffiliateCashout');
      const res = await fn({ amount: amt, method: cashoutMethod, notes: cashoutNotes || undefined });
      if (res?.data?.ok) {
        notify("Cashout request submitted for review", { type: "success" });
        setShowCashout(false);
        setCashoutAmount(0);
        setCashoutNotes("");
        await loadAffiliateData();
      } else {
        notify("Failed to submit cashout request", { type: "error" });
      }
    } catch (e) {
      console.error(e);
      notify(e?.message || "Cashout request failed", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const copyAffiliateLink = async () => {
    if (!affiliate) return;

    const baseUrl = window.location.origin;
    const affiliateLink = `${baseUrl}?ref=${affiliate.referralCode}`;
    
    try {
      await navigator.clipboard.writeText(affiliateLink);
      setCopiedLink(true);
      notify('Affiliate link copied to clipboard!', { type: 'success' });
      
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      notify('Failed to copy link', { type: 'error' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Firestore Timestamp object
    if (typeof timestamp === 'object' && timestamp !== null) {
      if (typeof timestamp.toDate === 'function') {
        try { return timestamp.toDate().toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }); } catch {}
      }
      if ('seconds' in timestamp) {
        try { return new Date(timestamp.seconds * 1000).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }); } catch {}
      }
      if ('_seconds' in timestamp) {
        try { return new Date(timestamp._seconds * 1000).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }); } catch {}
      }
    }
    // ISO string or millis
    try {
      const d = typeof timestamp === 'number' ? new Date(timestamp < 2e10 ? timestamp * 1000 : timestamp) : new Date(timestamp);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {}
    return '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Affiliate profile not found.</p>
      </div>
    );
  }

  // Derive payout availability
  const payout = affiliate.payout || {};
  const hasGCash = !!(payout.gcashNumber && String(payout.gcashNumber).trim());
  const hasPaymaya = !!(payout.paymayaNumber && String(payout.paymayaNumber).trim());
  const hasBank = !!(payout.bankName && payout.bankAccount && payout.accountName && String(payout.bankAccount).trim());
  const hasAnyPayout = hasGCash || hasPaymaya || hasBank;

  // Build allowed methods list based on configured payout accounts
  const availableMethods = [
    ...(hasGCash ? [{ value: 'gcash', label: 'GCash' }] : []),
    ...(hasPaymaya ? [{ value: 'paymaya', label: 'PayMaya' }] : []),
    ...(hasBank ? [{ value: 'bank', label: 'Bank Transfer' }] : []),
  ];

  // cashout method validation now handled inside loadAffiliateData after data fetch

  // Defensive fallbacks to avoid runtime crashes if backend shape changes
  const firstName = affiliate.userInfo?.firstName || 'Affiliate';
  const tier = affiliate.tier || 'bronze';

  return (
    <div className="space-y-6 px-3 sm:px-4 max-w-screen-lg mx-auto">
      {/* Header */}
  <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Affiliate Dashboard</h1>
          <p className="text-sm md:text-base text-gray-600">Welcome back, {firstName}!</p>
        </div>
        <div className="flex flex-col space-y-2 md:flex-row md:items-center md:gap-3 md:space-y-0">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
            tier === 'bronze' ? 'bg-orange-100 text-orange-800' :
            tier === 'silver' ? 'bg-gray-100 text-gray-800' :
            tier === 'gold' ? 'bg-yellow-100 text-yellow-800' :
            'bg-purple-100 text-purple-800'
          }`}>
            <Award className="w-4 h-4" />
            <span>{tier.charAt(0).toUpperCase() + tier.slice(1)} Tier</span>
          </div>
          <div className="flex items-center justify-between md:gap-3">
            <span className="text-xs md:text-sm text-gray-500">ID: {affiliate.affiliateId}</span>
            <button
              onClick={() => setShowTierSystem(!showTierSystem)}
              className="text-blue-600 hover:text-blue-800 text-xs md:text-sm font-medium"
            >
              View Progress
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
  <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">
                {formatCurrency(affiliate.totalEarnings)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600">Referrals</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">
                {affiliate.referralCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Sales</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">
                {formatCurrency(affiliate.totalSales)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-orange-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600">Commission Rate</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">
                {(affiliate.commissionRate * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payouts Summary - full width card for clarity */}
  <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start flex-col sm:flex-row">
            <div className="p-2 bg-teal-100 rounded-lg">
              <DollarSign className="h-5 w-5 md:h-6 md:w-6 text-teal-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600">Available for Cashout</p>
              <p className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 break-words">
                {formatCurrency(affiliate?.payouts?.availableAmount || 0)}
              </p>
              <p className="text-[12px] md:text-sm text-gray-500 mt-1">
                Pending: <span className="font-medium">{formatCurrency(affiliate?.payouts?.pendingAmount || 0)}</span> · Paid: <span className="font-medium">{formatCurrency(affiliate?.payouts?.paidAmount || 0)}</span>
              </p>
              <p className="text-[12px] md:text-sm text-gray-500 mt-1">Minimum cashout: {formatCurrency(50)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto sm:justify-end">
            <div className="flex flex-col items-start">
              <button
                onClick={() => setShowCashout(true)}
                disabled={!hasAnyPayout}
                className={`w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium text-center ${hasAnyPayout ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
              >
                Request Cashout
              </button>
              {!hasAnyPayout && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="mt-2 text-[11px] text-teal-700 hover:underline"
                >
                  Add a payout account in Settings first
                </button>
              )}
            </div>
          </div>
        </div>
        {payouts?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Recent Payout Requests</h4>
            <div className="space-y-2">
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{formatCurrency(p.amount || 0)}</p>
                    <p className="text-xs text-gray-600">{(p.method || '').replace('_', ' ')} · {p.status}</p>
                  </div>
                  <div className="text-xs text-gray-600">{p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000).toLocaleString('en-PH') : '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Affiliate Link Section */}
  <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
  <div className="flex flex-col space-y-2 md:flex-row md:items-center md:justify-between md:space-y-0 mb-4">
          <h2 className="text-base md:text-lg font-semibold text-gray-900">Your Affiliate Link</h2>
          <button
            onClick={copyAffiliateLink}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm md:text-base"
          >
            {copiedLink ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy Link
              </>
            )}
          </button>
        </div>
        
  <div className="bg-gray-50 p-3 md:p-4 rounded-lg overflow-x-auto">
          <p className="text-xs md:text-sm text-gray-600 mb-2">Share this link to earn commissions:</p>
          <p className="font-mono text-xs md:text-sm break-all">
            {window.location.origin}?ref={affiliate.referralCode}
          </p>
        </div>
        
        <div className="mt-4 text-xs md:text-sm text-gray-600 space-y-1">
          <p className="flex items-center gap-2">
            <span><strong>Your Referral Code:</strong> {affiliate.referralCode}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(affiliate.referralCode); notify('Referral code copied!', { type: 'success' }); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[12px] hover:bg-gray-50"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </p>
          <p><strong>Commission Rate:</strong> {(affiliate.commissionRate * 100).toFixed(1)}%</p>
        </div>
      </div>

      {/* Recent Activity */}
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Commissions */}
  <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Recent Commissions</h3>
          {commissions.length === 0 ? (
            <p className="text-gray-500 text-center py-4 text-sm">No commissions yet</p>
          ) : (
            <div className="space-y-3">
              {commissions.slice(0, 5).map((commission) => {
                const status = String(commission.status || '').toLowerCase();
                const friendly = status === 'pending' ? 'Earned' : (status.charAt(0).toUpperCase() + status.slice(1));
                return (
                  <div key={commission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm md:text-base truncate">{commission.productName || 'Commission'}</p>
                      <p className="text-xs md:text-sm text-gray-600">{formatDate(commission.createdAt)}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="font-semibold text-green-600 text-sm md:text-base">
                        {formatCurrency(commission.commissionAmount)}
                      </p>
                      <p className="text-xs md:text-sm text-gray-600">{friendly}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Referrals */}
  <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Recent Referrals</h3>
          {referrals.length === 0 ? (
            <p className="text-gray-500 text-center py-4 text-sm">No referrals yet</p>
          ) : (
            <div className="space-y-3">
              {referrals.slice(0, 5).map((referral) => (
                <div key={referral.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm md:text-base truncate">User {(referral.referredUserId || '').substring(0, 8) || '—'}</p>
                    <p className="text-xs md:text-sm text-gray-600">{formatDate(referral.createdAt)}</p>
                  </div>
                  <div className="text-right ml-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      referral.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {referral.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
  <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
          <button 
            onClick={() => setShowLiveSelling(!showLiveSelling)}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Video className="h-4 w-4 md:h-5 md:w-5 text-red-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm md:text-base">Live Selling</p>
              <p className="text-xs md:text-sm text-gray-600">Start live streams</p>
            </div>
          </button>
          
          <button 
            onClick={() => setShowProductLinkGenerator(!showProductLinkGenerator)}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Link className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm md:text-base">Product Link</p>
              <p className="text-xs md:text-sm text-gray-600">Create product links</p>
            </div>
          </button>
          
          <button 
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BarChart3 className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm md:text-base">Analytics</p>
              <p className="text-xs md:text-sm text-gray-600">Performance reports</p>
            </div>
          </button>
          
          <button 
            onClick={() => setShowTierSystem(!showTierSystem)}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Award className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm md:text-base">Tier System</p>
              <p className="text-xs md:text-sm text-gray-600">Track progression</p>
            </div>
          </button>
          
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm md:text-base">Settings</p>
              <p className="text-xs md:text-sm text-gray-600">Manage preferences</p>
            </div>
          </button>
        </div>
      </div>

      {/* Live Selling Platform */}
      {showLiveSelling && (
        <div className="mt-6">
          <LiveSellingPlatform affiliateId={affiliate.affiliateId} />
        </div>
      )}

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="mt-6">
          <AnalyticsDashboard affiliateId={affiliate.affiliateId} userId={userId} />
        </div>
      )}

      {/* Tier System */}
      {showTierSystem && (
        <div className="mt-6">
          <TierSystem affiliateId={affiliate.affiliateId} userId={userId} />
        </div>
      )}

      {/* Product Link Generator */}
      {showProductLinkGenerator && (
        <div className="mt-6">
          <ProductLinkGenerator 
            affiliateId={affiliate.affiliateId} 
            referralCode={affiliate.referralCode}
            userId={userId} 
          />
        </div>
      )}

      {/* Affiliate Settings */}
      {showSettings && (
        <div className="mt-6">
          <AffiliateSettings affiliateId={affiliate.affiliateId} userId={userId} />
        </div>
      )}

      {/* Cashout Modal */}
      {showCashout && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-2 pb-4 sm:pb-0">
          <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && setShowCashout(false)} />
          <div className="relative bg-white w-full sm:w-[92%] max-w-md rounded-t-lg sm:rounded-lg shadow-lg p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900">Request Cashout</h3>
            <p className="text-sm text-gray-600 mt-1">
              Available: {formatCurrency(affiliate?.payouts?.availableAmount || 0)} · Minimum: {formatCurrency(50)}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashoutAmount}
                  onChange={(e) => setCashoutAmount(e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="Enter amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Method</label>
                {availableMethods.length > 0 ? (
                  <select
                    value={cashoutMethod}
                    onChange={(e) => setCashoutMethod(e.target.value)}
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {availableMethods.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-1 text-xs text-red-600 font-medium">Configure a payout method in Settings to request a cashout.</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                <textarea
                  rows={3}
                  value={cashoutNotes}
                  onChange={(e) => setCashoutNotes(e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="e.g., GCash number or bank details if needed"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCashout(false)}
                disabled={submitting}
                className="px-3 py-2 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={requestCashout}
                disabled={submitting || !hasAnyPayout || availableMethods.length === 0}
                className="px-3 py-2 text-sm rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 w-full sm:w-auto"
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
